import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Mic } from 'lucide-react';
import { cn } from '@/lib/utils';

interface VoiceNotePlayerProps {
  url: string;
  duration: number;
  waveform?: number[];
  className?: string;
  isOwn?: boolean;
}

export const VoiceNotePlayer: React.FC<VoiceNotePlayerProps> = ({
  url,
  duration,
  waveform = [],
  className,
  isOwn = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Format duration as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle play/pause
  const togglePlayPause = async () => {
    if (!audioRef.current) {
      // Create audio element if it doesn't exist
      audioRef.current = new Audio(url);

      audioRef.current.addEventListener('loadstart', () => setIsLoading(true));
      audioRef.current.addEventListener('canplay', () => setIsLoading(false));

      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      });

      audioRef.current.addEventListener('error', (e) => {
        console.error('Audio playback error:', e);
        setIsPlaying(false);
        setIsLoading(false);
      });
    }

    if (isPlaying) {
      // Pause
      audioRef.current.pause();
      setIsPlaying(false);

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    } else {
      // Play
      try {
        setIsLoading(true);
        await audioRef.current.play();
        setIsPlaying(true);
        setIsLoading(false);

        // Update progress
        progressIntervalRef.current = setInterval(() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
          }
        }, 100);
      } catch (error) {
        console.error('Failed to play audio:', error);
        setIsPlaying(false);
        setIsLoading(false);
      }
    }
  };

  // Handle seek
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Generate default waveform if not provided
  const displayWaveform = waveform.length > 0
    ? waveform
    : Array.from({ length: 35 }, () => Math.random() * 0.6 + 0.2);

  return (
    <div
      className={cn(
        'relative flex items-center gap-2 p-2 rounded-lg',
        'max-w-[250px] min-w-[200px] w-full',
        // Make sure content is visible on both backgrounds
        isOwn
          ? 'bg-white/10' // Semi-transparent white on primary background
          : 'bg-black/5', // Semi-transparent black on muted background
        className
      )}
    >
      {/* Play/Pause button */}
      <button
        onClick={togglePlayPause}
        disabled={isLoading}
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          'transition-all duration-200',
          // Ensure button is visible
          isOwn
            ? 'bg-white/20 hover:bg-white/30 text-white'
            : 'bg-black/10 hover:bg-black/20 text-foreground'
        )}
      >
        {isLoading ? (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="h-4 w-4 rounded-full border-2 border-current border-t-transparent"
          />
        ) : isPlaying ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4 ml-0.5" />
        )}
      </button>

      {/* Waveform and progress */}
      <div className="flex-1 flex flex-col gap-1">
        {/* Waveform visualization */}
        <div
          className="relative h-7 flex items-center gap-[1px] cursor-pointer"
          onClick={handleSeek}
        >
          {displayWaveform.map((amplitude, i) => {
            const barProgress = (i / displayWaveform.length) * 100;
            const isActive = barProgress <= progress;

            return (
              <div
                key={i}
                className={cn(
                  'flex-1 rounded-full transition-all duration-100',
                  // Ensure bars are visible
                  isActive
                    ? isOwn
                      ? 'bg-white/90' // White bars on primary
                      : 'bg-foreground/70' // Dark bars on muted
                    : isOwn
                      ? 'bg-white/30' // Faded white on primary
                      : 'bg-foreground/20' // Faded dark on muted
                )}
                style={{
                  height: `${Math.max(2, amplitude * 20)}px`,
                }}
              />
            );
          })}
        </div>

        {/* Time display */}
        <div className="flex items-center justify-between text-[10px]">
          <span className={cn(
            'font-medium tabular-nums',
            isOwn ? 'text-white/70' : 'text-foreground/70'
          )}>
            {formatTime(currentTime)}
          </span>
          <div className="flex items-center gap-1">
            <Mic className={cn(
              'h-2.5 w-2.5',
              isOwn ? 'text-white/50' : 'text-muted-foreground'
            )} />
            <span className={cn(
              'font-medium tabular-nums',
              isOwn ? 'text-white/50' : 'text-muted-foreground'
            )}>
              {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};